import { Box, TextField, useTheme } from "@mui/material";
import { ClipboardEvent, KeyboardEvent, useMemo, useRef } from "react";

interface OtpCodeInputProps {
  value: string;
  onChange: (otp: string) => void;
  disabled?: boolean;
  error?: boolean;
  onComplete?: (otp: string) => void;
}

const OTP_LENGTH = 6;

const OtpCodeInput = ({
  value,
  onChange,
  disabled = false,
  error = false,
  onComplete,
}: OtpCodeInputProps) => {
  const theme = useTheme();
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const digits = useMemo(() => {
    const next = value.replace(/\D/g, "").slice(0, OTP_LENGTH);
    return Array.from({ length: OTP_LENGTH }, (_, index) => next[index] || "");
  }, [value]);

  const focusIndex = (index: number) => {
    const node = inputsRef.current[index];
    if (node) node.focus();
  };

  const emit = (nextValue: string) => {
    const sanitized = nextValue.replace(/\D/g, "").slice(0, OTP_LENGTH);
    onChange(sanitized);
    if (sanitized.length === OTP_LENGTH) {
      onComplete?.(sanitized);
    }
  };

  const handleChange = (index: number, rawValue: string) => {
    const raw = rawValue.replace(/\D/g, "");
    if (!raw) {
      const next = [...digits];
      next[index] = "";
      emit(next.join(""));
      return;
    }

    if (raw.length > 1) {
      emit(
        `${digits.slice(0, index).join("")}${raw}${digits.slice(index + raw.length).join("")}`,
      );
      focusIndex(Math.min(index + raw.length, OTP_LENGTH - 1));
      return;
    }

    const next = [...digits];
    next[index] = raw;
    emit(next.join(""));
    if (index < OTP_LENGTH - 1) focusIndex(index + 1);
  };

  const handleKeyDown = (index: number, event: KeyboardEvent) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      focusIndex(index - 1);
    }
    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      focusIndex(index - 1);
    }
    if (event.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      event.preventDefault();
      focusIndex(index + 1);
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    emit(event.clipboardData.getData("text"));
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
        gap: { xs: 0.75, sm: 1 },
        width: "100%",
        maxWidth: 360,
      }}
    >
      {digits.map((digit, index) => (
        <TextField
          key={index}
          value={digit}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
          disabled={disabled}
          error={error}
          inputRef={(node) => {
            inputsRef.current[index] = node;
          }}
          inputProps={{
            inputMode: "numeric",
            autoComplete: index === 0 ? "one-time-code" : "off",
            maxLength: 1,
            "aria-label": `Digit ${index + 1}`,
            style: {
              textAlign: "center",
              fontWeight: 700,
              fontSize: "1.15rem",
              padding: 0,
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: { xs: 44, sm: 48 },
              minHeight: { xs: 44, sm: 48 },
              borderRadius: "6px",
              backgroundColor: theme.palette.customColors.white,
            },
            "& .MuiOutlinedInput-input": {
              height: "100%",
              boxSizing: "border-box",
            },
          }}
        />
      ))}
    </Box>
  );
};

export default OtpCodeInput;
